import get from 'lodash/get';

// Every state the Active Magazine page can show. The numbers match the
// "Active Magazine" option B board (AM2-B).
export const MAGAZINE_STATES = {
  LOADING: 'loading',
  NOT_STARTED: 'not-started', // 1
  SAVED: 'saved', // 2
  PREVIEW_RUNNING: 'preview-running', // 3
  PREVIEW_READY: 'preview-ready', // 4
  SENT_TO_PRINT_NO_PAGE: 'sent-to-print-no-page', // 4b
  PUBLISHING: 'publishing', // 5 (5b once the 5-minute window is over)
  PUBLISHED: 'published', // 6
  RENDER_ERROR: 'render-error', // 7
  PAGE_ERROR: 'page-error', // 8
  UNAVAILABLE: 'unavailable', // a read failed for another reason than DS's 404
};

// DS's exception filter puts the message in `errors[0]`.
const getDsMessage = (error) =>
  get(error, 'response.data.errors[0]') ?? get(error, 'response.data.message');

export const isNotFoundError = (error) => get(error, 'response.status') === 404;

// Exact match: 'generated magazine not found' contains 'magazine not found'.
export const isNoMagazineError = (error) =>
  isNotFoundError(error) && getDsMessage(error) === 'magazine not found';

const isNoGenerationError = (error) =>
  isNotFoundError(error) && getDsMessage(error) === 'generated magazine not found';

// DS answers 404 when nothing exists yet: an answer, not a failure to retry.
export const retryUnlessNotFound = (failureCount, error) =>
  !isNotFoundError(error) && failureCount < 2;

const RUNNING = ['PENDING', 'PROCESSING'];
// The print-queue route writes SENT_FOR_PRINTING over a DONE render.
const RENDERED = ['DONE', 'SENT_FOR_PRINTING'];

/**
 * Decides what the page shows from its two reads:
 * - `published`: `GET generated-magazines/generated/:year/:month`, answered
 *   only for a generated magazine whose online page (`pageStatus`) is DONE;
 * - `latest`: `GET generated-magazines/:year/:month`, the member's active
 *   (latest) generation, re-used by every preview and publish run.
 * Each is `{ data, error, settled }`.
 */
export const getMagazineState = ({ published, latest }) => {
  if (get(published, 'data.id')) return MAGAZINE_STATES.PUBLISHED;
  if (isNoMagazineError(latest.error) || isNoMagazineError(published.error)) {
    return MAGAZINE_STATES.NOT_STARTED;
  }
  if (!published.settled || !latest.settled) return MAGAZINE_STATES.LOADING;
  if (published.error && !isNotFoundError(published.error)) return MAGAZINE_STATES.UNAVAILABLE;
  if (isNoGenerationError(latest.error)) return MAGAZINE_STATES.SAVED;
  // A failed refetch keeps the last data (react-query v3): decide from it.
  if (!latest.data) return MAGAZINE_STATES.UNAVAILABLE;

  const status = get(latest.data, 'status');
  const pageStatus = get(latest.data, 'pageStatus');
  const isPreview = get(latest.data, 'isPreview') === true;
  const isSentToPrint = get(latest.data, 'magazine.status') === 'SENT_FOR_PRINTING';

  // A preview made after the print send does not change what was printed.
  if (isPreview && isSentToPrint) return MAGAZINE_STATES.SENT_TO_PRINT_NO_PAGE;
  if (status === 'ERROR') return MAGAZINE_STATES.RENDER_ERROR;
  // A preview never runs the page step, so its `pageStatus` is left over from
  // an earlier publish and says nothing about this run.
  if (!isPreview) {
    // Nothing resets `pageStatus` when a run starts and the page step writes
    // it only when it ends, so this ERROR may be left over from an earlier
    // publish while this run's page step is running: the page keeps checking.
    if (RENDERED.includes(status) && pageStatus === 'ERROR') return MAGAZINE_STATES.PAGE_ERROR;
    // The render or the page step is running. A page that is already DONE
    // here means the published read came first: the next check finds it.
    return MAGAZINE_STATES.PUBLISHING;
  }
  if (RUNNING.includes(status)) return MAGAZINE_STATES.PREVIEW_RUNNING;
  return MAGAZINE_STATES.PREVIEW_READY;
};
